import { itemStatusSchedulingTypeSchema } from '@tempo-io/item-store-dto';
import { z } from 'zod';

const countSchema = z.number().int().nonnegative();

export const DEFAULT_EFFECTIVENESS_HEATMAP_WEEKS = 7;
export const MAX_EFFECTIVENESS_HEATMAP_WEEKS = 12;
export const DEFAULT_CONTENT_INTELLIGENCE_TIMELINE_PAGE_SIZE = 200;
export const MAX_CONTENT_INTELLIGENCE_TIMELINE_PAGE_SIZE = 500;
export const DEFAULT_CONTENT_INTELLIGENCE_RELATED_LIMIT = 50;
export const MAX_CONTENT_INTELLIGENCE_EVIDENCE_PAGE_SIZE = 100;
export const DEFAULT_CONTENT_INTELLIGENCE_METADATA_LIMIT = 50;
export const DEFAULT_CONTENT_INTELLIGENCE_GITHUB_LIMIT = 50;
export const MAX_CONTENT_INTELLIGENCE_DESCRIPTION_LENGTH = 10_000;
export const CONTENT_INTELLIGENCE_GITHUB_TEXT_LIMITS = {
  id: 255,
  sourceId: 200,
  repository: 255,
  actorGithubLogin: 100,
  jiraKey: 64,
  url: 512,
  commitHash: 128,
  branch: 255,
  summary: 2_000,
  reviewState: 64,
  truncatedField: 64,
} as const;
export const MAX_CONTENT_INTELLIGENCE_WEEKLY_SUMMARY_TEXT_LENGTH = 4_000;
export const MAX_CONTENT_INTELLIGENCE_WEEKLY_SUMMARY_EVIDENCE_IDS = 50;
export const MAX_CONTENT_INTELLIGENCE_WEEKLY_SUMMARY_BRIEF_CHARACTERS = 280;
export const CONTENT_INTELLIGENCE_TREND_WEEKS = 8;
export const CONTENT_INTELLIGENCE_WEEKLY_SUMMARY_STATUSES = [
  'succeeded',
  'unparseable',
  'retryable_error',
  'failed',
] as const;
export type ContentIntelligenceWeeklySummaryStatus =
  (typeof CONTENT_INTELLIGENCE_WEEKLY_SUMMARY_STATUSES)[number];

export const contentIntelligenceSyncUnavailableReasons = [
  'product_disabled',
  'scoring_not_configured',
  'organization_inactive',
] as const;

export const contentIntelligenceSyncAvailabilitySchema = z.discriminatedUnion('enabled', [
  z
    .object({
      enabled: z.literal(true),
      reason: z.null(),
    })
    .strict(),
  z
    .object({
      enabled: z.literal(false),
      reason: z.enum(contentIntelligenceSyncUnavailableReasons),
    })
    .strict(),
]);

export const contentIntelligenceEvaluationCapabilitiesResponseSchema = z
  .object({
    sync: contentIntelligenceSyncAvailabilitySchema,
  })
  .strict();

export type ContentIntelligenceSyncAvailability = z.infer<
  typeof contentIntelligenceSyncAvailabilitySchema
>;
export type ContentIntelligenceEvaluationCapabilitiesResponse = z.infer<
  typeof contentIntelligenceEvaluationCapabilitiesResponseSchema
>;

const countWeeklySummaryCharacters = (text: string): number => {
  return [...text].length;
};

export const contentIntelligenceWeeklySummaryBriefTextSchema = z
  .string()
  .trim()
  .min(1)
  .max(MAX_CONTENT_INTELLIGENCE_WEEKLY_SUMMARY_TEXT_LENGTH)
  .superRefine((text, context) => {
    if (
      countWeeklySummaryCharacters(text) > MAX_CONTENT_INTELLIGENCE_WEEKLY_SUMMARY_BRIEF_CHARACTERS
    ) {
      context.addIssue({
        code: 'custom',
        message: `Weekly summary brief must contain at most ${MAX_CONTENT_INTELLIGENCE_WEEKLY_SUMMARY_BRIEF_CHARACTERS} characters`,
      });
    }
  });

export const contentIntelligenceWeeklySummarySchema = z
  .object({
    briefText: contentIntelligenceWeeklySummaryBriefTextSchema,
    text: z.string().trim().min(1).max(MAX_CONTENT_INTELLIGENCE_WEEKLY_SUMMARY_TEXT_LENGTH),
    confidence: z.number().min(0).max(1),
    evidenceIds: z
      .array(z.string().trim().min(1).max(200))
      .max(MAX_CONTENT_INTELLIGENCE_WEEKLY_SUMMARY_EVIDENCE_IDS),
    weekStart: z.iso.date(),
    generatedAt: z.iso.datetime(),
    modelName: z.string().trim().min(1).max(200).nullable(),
    promptVersion: z.string().trim().min(1).max(100).nullable(),
  })
  .strict()
  .superRefine((summary, context) => {
    if ((summary.modelName === null) !== (summary.promptVersion === null)) {
      context.addIssue({
        code: 'custom',
        message: 'Weekly summary result provenance must be entirely known or unknown',
        path: ['modelName'],
      });
    }

    const seenEvidenceIds = new Set<string>();
    for (const [index, evidenceId] of summary.evidenceIds.entries()) {
      if (seenEvidenceIds.has(evidenceId)) {
        context.addIssue({
          code: 'custom',
          message: `Duplicate evidence id: ${evidenceId}`,
          path: ['evidenceIds', index],
        });
      }
      seenEvidenceIds.add(evidenceId);
    }
  });

export type ContentIntelligenceWeeklySummary = z.infer<
  typeof contentIntelligenceWeeklySummarySchema
>;

export const contentIntelligenceEffectivenessMetrics = [
  'delivery',
  'blockers',
  'ticket-quality',
  'code-alignment',
  'ai-findings',
] as const;

export const CONTENT_INTELLIGENCE_EFFECTIVENESS_SCORING_POLICY_VERSION =
  'effectiveness-v1' as const;

export type ContentIntelligenceEffectivenessMetric =
  (typeof contentIntelligenceEffectivenessMetrics)[number];

const contentIntelligenceEffectivenessMetricSchema = z.enum(
  contentIntelligenceEffectivenessMetrics,
);

export const contentIntelligenceEffectivenessHeatmapQuerySchema = z.object({
  weeks: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_EFFECTIVENESS_HEATMAP_WEEKS)
    .default(DEFAULT_EFFECTIVENESS_HEATMAP_WEEKS),
});

const effectivenessHeatmapCellSchema = z
  .object({
    score: z.number().min(0).max(1).nullable(),
    observationCount: countSchema,
    issueCount: countSchema,
  })
  .strict()
  .superRefine((cell, context) => {
    if (cell.observationCount === 0 && cell.score !== null) {
      context.addIssue({
        code: 'custom',
        message: 'An effectiveness score requires at least one observation',
        path: ['score'],
      });
    }
    if (cell.observationCount > 0 && cell.score === null) {
      context.addIssue({
        code: 'custom',
        message: 'Observed effectiveness must include a score',
        path: ['score'],
      });
    }
  });

export const contentIntelligenceEffectivenessHeatmapResponseSchema = z
  .object({
    loopId: z.number().int().positive(),
    scoringPolicyVersion: z.literal(CONTENT_INTELLIGENCE_EFFECTIVENESS_SCORING_POLICY_VERSION),
    weeks: z.array(z.iso.date()).min(1).max(MAX_EFFECTIVENESS_HEATMAP_WEEKS),
    rows: z.array(
      z
        .object({
          metric: contentIntelligenceEffectivenessMetricSchema,
          cells: z.array(effectivenessHeatmapCellSchema),
        })
        .strict(),
    ),
  })
  .strict()
  .superRefine((heatmap, context) => {
    const seenMetrics = new Set<ContentIntelligenceEffectivenessMetric>();

    for (const [index, row] of heatmap.rows.entries()) {
      if (seenMetrics.has(row.metric)) {
        context.addIssue({
          code: 'custom',
          message: `Duplicate effectiveness metric: ${row.metric}`,
          path: ['rows', index, 'metric'],
        });
      }
      seenMetrics.add(row.metric);

      if (row.cells.length !== heatmap.weeks.length) {
        context.addIssue({
          code: 'custom',
          message: 'Effectiveness cells must align with the requested weeks',
          path: ['rows', index, 'cells'],
        });
      }
    }

    for (const metric of contentIntelligenceEffectivenessMetrics) {
      if (!seenMetrics.has(metric)) {
        context.addIssue({
          code: 'custom',
          message: `Missing effectiveness metric: ${metric}`,
          path: ['rows'],
        });
      }
    }
  });

export type ContentIntelligenceEffectivenessHeatmap = z.infer<
  typeof contentIntelligenceEffectivenessHeatmapResponseSchema
>;

const normalizedMetricValueSchema = z.number().min(0).max(1);

export const contentIntelligenceTrendPointSchema = z
  .object({
    bucketStart: z.iso.date(),
    value: normalizedMetricValueSchema.nullable(),
    sampleSize: countSchema,
  })
  .strict()
  .superRefine((point, context) => {
    if (point.sampleSize === 0 && point.value !== null) {
      context.addIssue({
        code: 'custom',
        message: 'An empty trend bucket must have a null value',
        path: ['value'],
      });
    }
    if (point.sampleSize > 0 && point.value === null) {
      context.addIssue({
        code: 'custom',
        message: 'An observed trend bucket must have a value',
        path: ['value'],
      });
    }
  });

export const contentIntelligenceTrendSeriesSchema = z
  .object({
    current: normalizedMetricValueSchema.nullable(),
    points: z.array(contentIntelligenceTrendPointSchema).length(CONTENT_INTELLIGENCE_TREND_WEEKS),
    modelName: z.string().trim().min(1).max(200).nullable(),
    promptVersion: z.string().trim().min(1).max(200).nullable(),
    pipelineVersion: z.string().trim().min(1).max(200).nullable(),
    mixedVersions: z.boolean(),
  })
  .strict()
  .superRefine((series, context) => {
    let latestObserved: number | null = null;
    let previousBucketStart: number | null = null;
    for (const [index, point] of series.points.entries()) {
      const bucketStart = Date.parse(`${point.bucketStart}T00:00:00.000Z`);
      if (previousBucketStart !== null) {
        const oneWeekMs = 7 * 24 * 60 * 60 * 1_000;
        if (bucketStart - previousBucketStart !== oneWeekMs) {
          context.addIssue({
            code: 'custom',
            message: 'Trend buckets must be consecutive Monday-UTC weeks',
            path: ['points', index, 'bucketStart'],
          });
        }
      }
      if (new Date(bucketStart).getUTCDay() !== 1) {
        context.addIssue({
          code: 'custom',
          message: 'Trend buckets must start on Monday UTC',
          path: ['points', index, 'bucketStart'],
        });
      }
      previousBucketStart = bucketStart;
      if (point.value !== null) {
        latestObserved = point.value;
      }
    }
    if (series.current !== null && series.current !== latestObserved) {
      context.addIssue({
        code: 'custom',
        message: 'A current value must equal the latest non-null trend value',
        path: ['current'],
      });
    }
  });

export const contentIntelligenceOverviewMetricsSchema = z
  .object({
    ticketQualityScore: contentIntelligenceTrendSeriesSchema,
    ticketQualityConfidence: contentIntelligenceTrendSeriesSchema,
    ticketQualityMovement: contentIntelligenceTrendSeriesSchema,
    gitWorkMismatchRate: contentIntelligenceTrendSeriesSchema,
  })
  .strict();

export type ContentIntelligenceTrendPoint = z.infer<typeof contentIntelligenceTrendPointSchema>;
export type ContentIntelligenceTrendSeries = z.infer<typeof contentIntelligenceTrendSeriesSchema>;
export type ContentIntelligenceOverviewMetrics = z.infer<
  typeof contentIntelligenceOverviewMetricsSchema
>;

export const contentIntelligenceOverviewResponseSchema = z.object({
  loopId: z.number().int().positive(),
  weeklySummary: contentIntelligenceWeeklySummarySchema.nullable(),
  metrics: contentIntelligenceOverviewMetricsSchema,
  signals: z.object({
    total: countSchema,
  }),
  insights: z.object({
    total: countSchema,
    byStatus: z.object({
      draft: countSchema,
      published: countSchema,
      dismissed: countSchema,
    }),
    byValence: z.object({
      risk: countSchema,
      opportunity: countSchema,
    }),
  }),
  recommendations: z.object({
    total: countSchema,
    byStatus: z.object({
      pending: countSchema,
      accepted: countSchema,
      dismissed: countSchema,
      completed: countSchema,
    }),
    byPriority: z.object({
      low: countSchema,
      medium: countSchema,
      high: countSchema,
      urgent: countSchema,
    }),
  }),
});

export type ContentIntelligenceOverview = z.infer<typeof contentIntelligenceOverviewResponseSchema>;

export const contentIntelligenceTimelineQuerySchema = z.object({
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_CONTENT_INTELLIGENCE_TIMELINE_PAGE_SIZE)
    .default(DEFAULT_CONTENT_INTELLIGENCE_TIMELINE_PAGE_SIZE),
  cursor: z.string().min(1).max(2_000).optional(),
});

const contentIntelligenceNullableDateTimeSchema = z.iso.datetime().nullable();

const contentIntelligenceItemTypeSchema = z.object({
  id: z.number().int().positive(),
  name: z.string().nullable(),
  level: z.number().int().nullable(),
});

const contentIntelligenceItemStatusSchema = z.object({
  id: z.number().int().positive(),
  name: z.string().nullable(),
  color: z.string().nullable(),
  schedulingType: itemStatusSchedulingTypeSchema,
});

export const contentIntelligenceTimelineItemSchema = z.object({
  id: z.number().int().positive(),
  parentId: z.number().int().positive().nullable(),
  depth: z.number().int().nonnegative().nullable(),
  childCount: z.number().int().nonnegative(),
  childCountIsLowerBound: z.boolean(),
  externalKey: z.string().nullable(),
  externalLink: z.string().nullable(),
  name: z.string().nullable(),
  itemType: contentIntelligenceItemTypeSchema,
  status: contentIntelligenceItemStatusSchema,
  plannedStart: contentIntelligenceNullableDateTimeSchema,
  plannedFinish: contentIntelligenceNullableDateTimeSchema,
  earliestStart: contentIntelligenceNullableDateTimeSchema,
  deadline: contentIntelligenceNullableDateTimeSchema,
  doneAt: contentIntelligenceNullableDateTimeSchema,
  updatedAt: contentIntelligenceNullableDateTimeSchema,
  timeSpentSeconds: z.number().int().nullable(),
  estimated: z.boolean(),
});

export const contentIntelligenceTimelineResponseSchema = z
  .object({
    loopId: z.number().int().positive(),
    totalItems: z.number().int().nonnegative().nullable(),
    returnedItems: z.number().int().nonnegative().max(MAX_CONTENT_INTELLIGENCE_TIMELINE_PAGE_SIZE),
    truncated: z.boolean(),
    hierarchyComplete: z.boolean(),
    nextCursor: z.string().min(1).max(2_000).optional(),
    items: z
      .array(contentIntelligenceTimelineItemSchema)
      .max(MAX_CONTENT_INTELLIGENCE_TIMELINE_PAGE_SIZE),
    dataSource: z.literal('item-store'),
  })
  .superRefine((response, context) => {
    if (response.returnedItems !== response.items.length) {
      context.addIssue({
        code: 'custom',
        message: 'Returned item count must equal the number of timeline items',
        path: ['returnedItems'],
      });
    }
    if (response.totalItems !== null && response.totalItems < response.returnedItems) {
      context.addIssue({
        code: 'custom',
        message: 'Total item count cannot be lower than the returned item count',
        path: ['totalItems'],
      });
    }
    const hasNextCursor = response.nextCursor !== undefined;
    if (response.truncated !== hasNextCursor) {
      context.addIssue({
        code: 'custom',
        message: 'Next cursor must be present if and only if the timeline is truncated',
        path: ['nextCursor'],
      });
    }
  });

export type ContentIntelligenceTimeline = z.infer<typeof contentIntelligenceTimelineResponseSchema>;
export type ContentIntelligenceTimelineItem = z.infer<typeof contentIntelligenceTimelineItemSchema>;

export const contentIntelligenceEvidenceQuerySchema = z.object({
  relatedLimit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_CONTENT_INTELLIGENCE_EVIDENCE_PAGE_SIZE)
    .default(DEFAULT_CONTENT_INTELLIGENCE_RELATED_LIMIT),
  metadataLimit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_CONTENT_INTELLIGENCE_EVIDENCE_PAGE_SIZE)
    .default(DEFAULT_CONTENT_INTELLIGENCE_METADATA_LIMIT),
  githubLimit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_CONTENT_INTELLIGENCE_EVIDENCE_PAGE_SIZE)
    .default(DEFAULT_CONTENT_INTELLIGENCE_GITHUB_LIMIT),
  githubCursor: z.string().min(1).max(2_048).optional(),
});

export const contentIntelligenceEvidenceItemSchema = z.object({
  id: z.number().int().positive(),
  loopId: z.number().int().positive(),
  parentId: z.number().int().positive().nullable(),
  name: z.string().nullable(),
  description: z.string().max(MAX_CONTENT_INTELLIGENCE_DESCRIPTION_LENGTH).nullable(),
  priority: z.string(),
  teamId: z.number().int().positive().nullable(),
  estimationId: z.number().int().positive().nullable(),
  timeSpentSeconds: z.number().int().nullable(),
  externalKey: z.string().nullable(),
  externalLink: z.string().nullable(),
  itemType: contentIntelligenceItemTypeSchema,
  status: contentIntelligenceItemStatusSchema,
  dates: z.object({
    earliestStart: contentIntelligenceNullableDateTimeSchema,
    deadline: contentIntelligenceNullableDateTimeSchema,
    plannedStart: contentIntelligenceNullableDateTimeSchema,
    plannedFinish: contentIntelligenceNullableDateTimeSchema,
    doneAt: contentIntelligenceNullableDateTimeSchema,
    createdAt: contentIntelligenceNullableDateTimeSchema,
    updatedAt: contentIntelligenceNullableDateTimeSchema,
  }),
});

const contentIntelligenceScheduleSchema = z.discriminatedUnion('availability', [
  z.object({
    availability: z.literal('not_available'),
  }),
  z.object({
    availability: z.literal('available'),
    expectedStart: z.iso.datetime(),
    expectedFinish: z.iso.datetime(),
    latestFinish: z.iso.datetime(),
  }),
]);

const contentIntelligenceSprintSchema = z.object({
  sprintId: z.number().int().positive(),
  boardId: z.number().int().positive(),
  detailsAvailability: z.enum(['available', 'not_found']),
  name: z.string().nullable(),
  state: z.string().nullable(),
  startAt: z.iso.datetime().nullable(),
  endAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
});

const contentIntelligenceGithubActivitySchema = z.object({
  id: z.string().min(1).max(CONTENT_INTELLIGENCE_GITHUB_TEXT_LIMITS.id),
  sourceId: z.string().min(1).max(CONTENT_INTELLIGENCE_GITHUB_TEXT_LIMITS.sourceId),
  type: z.enum(['pull_request_event', 'pull_request_review', 'commit']),
  repository: z.string().min(1).max(CONTENT_INTELLIGENCE_GITHUB_TEXT_LIMITS.repository),
  actorGithubLogin: z
    .string()
    .min(1)
    .max(CONTENT_INTELLIGENCE_GITHUB_TEXT_LIMITS.actorGithubLogin)
    .optional(),
  jiraKeys: z
    .array(z.string().min(1).max(CONTENT_INTELLIGENCE_GITHUB_TEXT_LIMITS.jiraKey))
    .min(1)
    .max(100),
  occurredAt: z.iso.datetime(),
  url: z.url().max(CONTENT_INTELLIGENCE_GITHUB_TEXT_LIMITS.url),
  pullRequestEvent: z
    .enum(['opened', 'merged', 'closed', 'ready_for_review', 'converted_to_draft'])
    .optional(),
  pullRequestNumber: z.number().int().positive(),
  commitHash: z.string().min(1).max(CONTENT_INTELLIGENCE_GITHUB_TEXT_LIMITS.commitHash).optional(),
  branch: z.string().min(1).max(CONTENT_INTELLIGENCE_GITHUB_TEXT_LIMITS.branch).optional(),
  summary: z.string().min(1).max(CONTENT_INTELLIGENCE_GITHUB_TEXT_LIMITS.summary).optional(),
  additions: z.number().int().nonnegative(),
  deletions: z.number().int().nonnegative(),
  filesChanged: z.number().int().nonnegative().optional(),
  reviewState: z
    .string()
    .min(1)
    .max(CONTENT_INTELLIGENCE_GITHUB_TEXT_LIMITS.reviewState)
    .optional(),
  reviewerIsBot: z.boolean().optional(),
  truncatedFields: z
    .array(z.string().min(1).max(CONTENT_INTELLIGENCE_GITHUB_TEXT_LIMITS.truncatedField))
    .max(4),
});

const contentIntelligenceGithubEvidenceSchema = z
  .discriminatedUnion('availability', [
    z.object({
      availability: z.literal('missing_external_key'),
      returned: z.literal(0),
      truncated: z.literal(false),
      items: z.array(contentIntelligenceGithubActivitySchema).max(0),
    }),
    z.object({
      availability: z.literal('non_unique_external_key'),
      returned: z.literal(0),
      truncated: z.literal(false),
      items: z.array(contentIntelligenceGithubActivitySchema).max(0),
    }),
    z.object({
      availability: z.literal('available'),
      returned: z.number().int().nonnegative().max(MAX_CONTENT_INTELLIGENCE_EVIDENCE_PAGE_SIZE),
      truncated: z.boolean(),
      nextCursor: z.string().min(1).max(2_048).optional(),
      items: z
        .array(contentIntelligenceGithubActivitySchema)
        .max(MAX_CONTENT_INTELLIGENCE_EVIDENCE_PAGE_SIZE),
    }),
  ])
  .superRefine((evidence, context) => {
    if (evidence.availability !== 'available') return;
    if (evidence.returned !== evidence.items.length) {
      context.addIssue({
        code: 'custom',
        message: 'Returned GitHub activity count must equal the number of items',
        path: ['returned'],
      });
    }
    const hasNextCursor = evidence.nextCursor !== undefined;
    if (evidence.truncated !== hasNextCursor) {
      context.addIssue({
        code: 'custom',
        message: 'Next cursor must be present if and only if GitHub activity is truncated',
        path: ['nextCursor'],
      });
    }
  });

export const contentIntelligenceEvidenceResponseSchema = z
  .object({
    loopId: z.number().int().positive(),
    item: contentIntelligenceEvidenceItemSchema,
    hierarchy: z.object({
      ancestors: z
        .array(contentIntelligenceEvidenceItemSchema)
        .max(MAX_CONTENT_INTELLIGENCE_EVIDENCE_PAGE_SIZE),
      ancestorsTruncated: z.boolean(),
      directChildren: z
        .array(contentIntelligenceEvidenceItemSchema)
        .max(MAX_CONTENT_INTELLIGENCE_EVIDENCE_PAGE_SIZE),
      directChildIds: z
        .array(z.number().int().positive())
        .max(MAX_CONTENT_INTELLIGENCE_EVIDENCE_PAGE_SIZE),
      directChildCount: z.number().int().nonnegative(),
      directChildCountIsLowerBound: z.boolean(),
      returnedDirectChildren: z
        .number()
        .int()
        .nonnegative()
        .max(MAX_CONTENT_INTELLIGENCE_EVIDENCE_PAGE_SIZE),
      childrenTruncated: z.boolean(),
    }),
    dependencies: z.object({
      returned: z.number().int().nonnegative().max(MAX_CONTENT_INTELLIGENCE_EVIDENCE_PAGE_SIZE),
      truncated: z.boolean(),
      items: z
        .array(
          z.object({
            id: z.number().int().positive(),
            relationship: z.enum(['blocks', 'blocked_by']),
            createdAt: contentIntelligenceNullableDateTimeSchema,
            updatedAt: contentIntelligenceNullableDateTimeSchema,
            item: contentIntelligenceEvidenceItemSchema.nullable(),
          }),
        )
        .max(MAX_CONTENT_INTELLIGENCE_EVIDENCE_PAGE_SIZE),
    }),
    schedule: contentIntelligenceScheduleSchema,
    sprints: z.object({
      availability: z.enum(['available', 'not_integrated']),
      returned: z.number().int().nonnegative().max(MAX_CONTENT_INTELLIGENCE_EVIDENCE_PAGE_SIZE),
      truncated: z.boolean(),
      items: z
        .array(contentIntelligenceSprintSchema)
        .max(MAX_CONTENT_INTELLIGENCE_EVIDENCE_PAGE_SIZE),
    }),
    githubEvidence: contentIntelligenceGithubEvidenceSchema,
    dataSources: z.object({
      workItems: z.literal('item-store'),
      github: z.literal('effort-spend'),
    }),
  })
  .superRefine((response, context) => {
    const collections = [
      {
        returned: response.hierarchy.returnedDirectChildren,
        itemCount: response.hierarchy.directChildren.length,
        path: ['hierarchy', 'returnedDirectChildren'],
        label: 'direct children',
      },
      {
        returned: response.dependencies.returned,
        itemCount: response.dependencies.items.length,
        path: ['dependencies', 'returned'],
        label: 'dependencies',
      },
      {
        returned: response.sprints.returned,
        itemCount: response.sprints.items.length,
        path: ['sprints', 'returned'],
        label: 'sprints',
      },
    ] as const;
    for (const collection of collections) {
      if (collection.returned !== collection.itemCount) {
        context.addIssue({
          code: 'custom',
          message: `Returned ${collection.label} count must equal the number of items`,
          path: [...collection.path],
        });
      }
    }

    if (
      response.hierarchy.returnedDirectChildren !== response.hierarchy.directChildIds.length ||
      response.hierarchy.returnedDirectChildren !== response.hierarchy.directChildCount
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Direct child IDs, count, and returned item count must agree',
        path: ['hierarchy', 'directChildCount'],
      });
    }
    if (response.hierarchy.directChildCountIsLowerBound !== response.hierarchy.childrenTruncated) {
      context.addIssue({
        code: 'custom',
        message: 'Direct child count is a lower bound if and only if children are truncated',
        path: ['hierarchy', 'directChildCountIsLowerBound'],
      });
    }
    const directChildIds = response.hierarchy.directChildren.map((child) => child.id);
    if (directChildIds.some((id, index) => id !== response.hierarchy.directChildIds[index])) {
      context.addIssue({
        code: 'custom',
        message: 'Direct child IDs must align with the returned child items',
        path: ['hierarchy', 'directChildIds'],
      });
    }

    if (
      response.sprints.availability === 'not_integrated' &&
      (response.sprints.returned !== 0 ||
        response.sprints.truncated ||
        response.sprints.items.length !== 0)
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Sprint evidence must be empty when no sprint integration is available',
        path: ['sprints'],
      });
    }

    const evidenceItems = [
      { item: response.item, path: ['item', 'loopId'] },
      ...response.hierarchy.ancestors.map((item, index) => ({
        item,
        path: ['hierarchy', 'ancestors', index, 'loopId'],
      })),
      ...response.hierarchy.directChildren.map((item, index) => ({
        item,
        path: ['hierarchy', 'directChildren', index, 'loopId'],
      })),
      ...response.dependencies.items.flatMap((dependency, index) => {
        if (dependency.item === null) return [];
        return [
          { item: dependency.item, path: ['dependencies', 'items', index, 'item', 'loopId'] },
        ];
      }),
    ];
    for (const evidenceItem of evidenceItems) {
      if (evidenceItem.item.loopId !== response.loopId) {
        context.addIssue({
          code: 'custom',
          message: 'Evidence item loop must match the requested loop',
          path: evidenceItem.path,
        });
      }
    }
  });

export type ContentIntelligenceEvidence = z.infer<typeof contentIntelligenceEvidenceResponseSchema>;
export type ContentIntelligenceEvidenceItem = z.infer<typeof contentIntelligenceEvidenceItemSchema>;
